import { create } from 'zustand'
import { startOfWeek, today } from '@/logic/dates'
import type { DateStr, TaskKind } from '@/types'

// Interface state for this visit: the toast, quick add, the week the Plan tab
// shows, the Calendar's day and mode, the open sheets, and the task just added.
// It lives here rather than in the views so opening a task over a view, which
// rebuilds nothing, keeps every one of them as it was.

export interface Toast {
  id: number
  text: string
  /** The button beside the text: Undo, or Show for a task added out of view. */
  action: { label: string; run: () => void | Promise<void> } | null
}

/** Where a new task lands, from where quick add was opened. */
export interface AddContext {
  plannedDate?: DateStr | null
  kind?: TaskKind
  /** The placeholder: "Add for Thu 15 Oct", "Add a routine". */
  hint?: string
}

export type CalendarMode = 'day' | 'week' | 'month'

/** A Google event opened from a chip or a grid: the card shows it, read-only. */
export interface OpenEvent {
  calendarId: string
  eventId: string
}

interface UiState {
  toast: Toast | null
  quickAddOpen: boolean
  addContext: AddContext
  /** What the current view wants a new task to start with: the Calendar sets its selected day. */
  defaultAddContext: AddContext
  online: boolean
  /** The Monday of the week the Plan tab shows. */
  planWeek: DateStr
  /** Null until the Calendar first opens, then Day on a phone or Month on a wide screen. */
  calendarMode: CalendarMode | null
  calendarDate: DateStr
  /** The Day view's tasks panel, opened for the rest of the visit. */
  dayTasksOpen: boolean
  /** Days whose events are opened under their heading, with the Events setting on Count. */
  openEventDays: DateStr[]
  searchOpen: boolean
  filtersOpen: boolean
  openEvent: OpenEvent | null
  /** A task to bring into view and play the sheen on. */
  highlight: { id: string; nonce: number } | null
  /** Send feedback, opened from the More menu or Settings, over whatever view is showing. */
  feedbackOpen: boolean
  /** The Plan tab's Select mode: cards tick in and out of a selection instead of opening. */
  selecting: boolean
  /** The selected tasks' ids, in the order they were picked. */
  selected: string[]
}

export const useUi = create<UiState>()(() => ({
  toast: null,
  quickAddOpen: false,
  addContext: {},
  defaultAddContext: {},
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  planWeek: startOfWeek(today()),
  calendarMode: null,
  calendarDate: today(),
  dayTasksOpen: false,
  openEventDays: [],
  searchOpen: false,
  filtersOpen: false,
  openEvent: null,
  highlight: null,
  feedbackOpen: false,
  selecting: false,
  selected: [],
}))

let toastTimer: number | undefined
let toastSeq = 0

/** Shows the one toast. An `undo` callback gives it an Undo button. */
export function showToast(text: string, undo: (() => void | Promise<void>) | null = null, ms = 6000): void {
  showToastAction(text, undo ? { label: 'Undo', run: undo } : null, ms)
}

export function showToastAction(text: string, action: Toast['action'], ms = 6000): void {
  window.clearTimeout(toastTimer)
  toastSeq += 1
  useUi.setState({ toast: { id: toastSeq, text, action } })
  toastTimer = window.setTimeout(() => useUi.setState({ toast: null }), ms)
}

export function dismissToast(): void {
  window.clearTimeout(toastTimer)
  useUi.setState({ toast: null })
}

export function openQuickAdd(context?: AddContext): void {
  useUi.setState((s) => ({ quickAddOpen: true, addContext: context ?? s.defaultAddContext }))
}

export function closeQuickAdd(): void {
  useUi.setState({ quickAddOpen: false, addContext: {} })
}

export function setDefaultAddContext(context: AddContext): void {
  useUi.setState((s) => ({ defaultAddContext: context, addContext: s.quickAddOpen ? s.addContext : context }))
}

export function setPlanWeek(weekStart: DateStr): void {
  useUi.setState({ planWeek: weekStart })
}

export function setCalendarMode(mode: CalendarMode): void {
  useUi.setState({ calendarMode: mode })
}

export function setCalendarDate(date: DateStr): void {
  useUi.setState({ calendarDate: date })
}

export function toggleEventDay(date: DateStr): void {
  useUi.setState((s) => ({ openEventDays: s.openEventDays.includes(date) ? s.openEventDays.filter((d) => d !== date) : [...s.openEventDays, date] }))
}

export function setSearchOpen(open: boolean): void {
  useUi.setState({ searchOpen: open })
}

export function setFiltersOpen(open: boolean): void {
  useUi.setState({ filtersOpen: open })
}

export function openEventCard(ev: OpenEvent | null): void {
  useUi.setState({ openEvent: ev })
}

export function setFeedbackOpen(open: boolean): void {
  useUi.setState({ feedbackOpen: open })
}

export function startSelecting(): void {
  useUi.setState({ selecting: true, selected: [], quickAddOpen: false })
}

export function stopSelecting(): void {
  useUi.setState({ selecting: false, selected: [] })
}

export function toggleSelected(id: string): void {
  useUi.setState((s) => ({ selected: s.selected.includes(id) ? s.selected.filter((x) => x !== id) : [...s.selected, id] }))
}

export function highlightTask(id: string): void {
  useUi.setState({ highlight: { id, nonce: Date.now() } })
}

export function startOnlineListener(): () => void {
  const on = () => useUi.setState({ online: true })
  const off = () => useUi.setState({ online: false })
  window.addEventListener('online', on)
  window.addEventListener('offline', off)
  return () => {
    window.removeEventListener('online', on)
    window.removeEventListener('offline', off)
  }
}
